import { useCallback, useEffect, useRef, useState } from "react";
import { Mail, Reply, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ukDateTime } from "./crmPipelineShared";
import { errMsg } from "./LoadError";

const TONE = { sent: "bg-emerald-500/15 text-emerald-200", received: "bg-blue-500/15 text-blue-200", failed: "bg-red-500/15 text-red-200",
  bounced: "bg-red-500/15 text-red-200", queued: "bg-white/10 text-emerald-100/70" };
const LABEL = { sent: "Sent (delivery not confirmed)", received: "Reply received", failed: "Failed", bounced: "Bounced", queued: "Queued" };
const inputCls = "w-full bg-[#091E16] border border-white/15 rounded-lg px-3 py-2 text-base sm:text-sm text-white outline-none focus:border-brass";

export function EmailPanel({ store, id, hasEmail, onSent }) {
  const [data, setData] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [busy, setBusy] = useState(false);
  const keyRef = useRef(null);
  const load = useCallback(() => api.get("/crm/emails", { params: { store, id } }).then((r) => setData(r.data)).catch(() => setData({ emails: [] })), [store, id]);
  useEffect(() => { load(); }, [load]);

  const reply = (e) => { setReplyTo(e.id); setSubject(e.subject?.startsWith("Re:") ? e.subject : `Re: ${e.subject || ""}`); document.querySelector('[data-testid="email-body"]')?.focus(); };
  const send = async () => {
    if (!subject.trim() || !body.trim()) return toast.error("Add a subject and a message");
    keyRef.current = keyRef.current || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setBusy(true);
    try {
      await api.post("/crm/emails/send", { store, id, subject, body, reply_to_email_id: replyTo, client_key: keyRef.current });
      toast.success("Email sent"); setSubject(""); setBody(""); setReplyTo(null); keyRef.current = null; load(); onSent && onSent();
    } catch (e) { toast.error(errMsg(e, "Email not sent")); keyRef.current = null; }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5" data-testid="email-panel">
      <div className="flex items-center gap-2 mb-3"><Mail className="w-4 h-4 text-brass" /><h3 className="text-sm font-semibold text-white">Emails</h3>
        {data && !data.mailbox_connected && <span className="text-[10px] text-amber-200/80 ml-auto" data-testid="email-mailbox-off">hello@ mailbox not connected yet</span>}</div>
      {hasEmail ? (
        <div className="space-y-2 mb-4" data-testid="email-compose">
          {replyTo && <div className="text-[11px] text-brass flex items-center gap-1"><Reply className="w-3 h-3" />Replying in thread <button onClick={() => setReplyTo(null)} className="underline ml-1">cancel</button></div>}
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className={inputCls} data-testid="email-subject" />
          <textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write your message…" className={inputCls} data-testid="email-body" />
          <button onClick={send} disabled={busy} data-testid="email-send" className="inline-flex items-center gap-1.5 bg-brass text-emerald-950 font-semibold rounded-lg px-4 h-10 text-sm disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}{busy ? "Sending…" : data?.mailbox_connected ? "Send from hello@" : "Send email"}
          </button>
        </div>
      ) : <p className="text-xs text-emerald-100/50 mb-3">No email address on this customer.</p>}
      <ol className="space-y-2" data-testid="email-thread">
        {(data?.emails || []).map((e) => (
          <li key={e.id} className="bg-white/[0.03] border border-white/5 rounded-lg p-3" data-testid={`email-${e.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${TONE[e.status] || TONE.queued}`} data-testid={`email-status-${e.id}`}>{LABEL[e.status] || e.status}</span>
              <span className="text-[11px] text-emerald-100/50">{e.direction === "in" ? `From ${e.from}` : `To ${e.to}`} · {ukDateTime(e.created_at)}</span>
              {e.direction === "in" && <button onClick={() => reply(e)} data-testid={`email-reply-${e.id}`} className="ml-auto text-[11px] text-brass flex items-center gap-1"><Reply className="w-3 h-3" />Reply</button>}
            </div>
            <div className="text-sm text-white mt-1.5">{e.subject}</div>
            {e.body && <p className="text-xs text-emerald-100/70 mt-1 whitespace-pre-wrap line-clamp-6">{e.body}</p>}
            {e.error && <p className="text-[11px] text-red-300 mt-1">{e.error}</p>}
          </li>
        ))}
        {data && data.emails.length === 0 && <li className="text-xs text-emerald-100/40" data-testid="email-empty">No emails yet.</li>}
      </ol>
    </div>
  );
}
